import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToken } from "@/lib/session";
import { toast } from "sonner";
import { Pencil, ArrowLeft, Users, Trophy, Send, Play, FastForward, Eraser } from "lucide-react";
import { api, API } from "@/lib/api";

const ROUND_TIPS = ["Don't write the word", "Drawing only — no letters!", "Use the colors to your advantage"];

export default function PictionaryPage() {
  const navigate = useNavigate();
  const token = useToken();

  const [room, setRoom] = useState(null);
  const [mode, setMode] = useState("lobby"); // lobby selection screen
  const [joinCode, setJoinCode] = useState("");
  const [guess, setGuess] = useState("");
  const [chat, setChat] = useState([]); // {from, text, correct, ts}
  const [sprint, setSprint] = useState(null); // {secondsAt:number, total:number}
  const [color, setColor] = useState("#000000");
  const [brushSize, setBrushSize] = useState(4);
  const [timeLeft, setTimeLeft] = useState(0);

  const wsRef = useRef(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  const remoteStrokes = useRef([]); // queue of incoming strokes to draw

  const me = room?.players?.find((p) => p.is_self);
  const isDrawer = me?.is_drawer;
  const isHost = me?.user_id === room?.host_id;
  const inRound = room?.state === "drawing";

  // ---------------- Room actions ----------------

  const createRoom = async () => {
    try {
      const r = await api.post(`/pictionary/room/create?token=${token}`, { max_rounds: 5 });
      setRoom(r.data);
      setMode("room");
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const joinRoom = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    try {
      const r = await api.post(`/pictionary/room/${code}/join?token=${token}`);
      setRoom(r.data);
      setMode("room");
    } catch (e) { toast.error(e?.response?.data?.detail || "Join failed"); }
  };

  const startGame = async () => {
    try { await api.post(`/pictionary/room/${room.code}/start?token=${token}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Start failed"); }
  };

  const nextRound = async () => {
    try { await api.post(`/pictionary/room/${room.code}/next-round?token=${token}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitGuess = async () => {
    const t = guess.trim();
    if (!t || isDrawer || !inRound) return;
    setGuess("");
    try { await api.post(`/pictionary/room/${room.code}/guess?token=${token}`, { text: t }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Guess failed"); }
  };

  // ---------------- WebSocket ----------------

  useEffect(() => {
    if (!room?.code) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = base.replace(/^http/, "ws");
    const url = `${wsBase}/api/pictionary/ws/room/${room.code}?token=${encodeURIComponent(token)}`;
    let ws;
    let retry;
    let cancelled = false;
    const refetch = async () => {
      try {
        const r = await api.get(`/pictionary/room/${room.code}?token=${token}`);
        setRoom(r.data);
      } catch { /* ignore */ }
    };
    const connect = () => {
      if (cancelled) return;
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "state" && msg.state) {
            setRoom((cur) => ({ ...(cur || {}), ...msg.state }));
          } else if (msg.type === "round_start" || msg.type === "round_end") {
            refetch();
            if (msg.type === "round_start") {
              setChat([]);
              setSprint(null);
            }
          } else if (msg.type === "sprint_countdown") {
            setSprint({ secondsAt: Date.now() + (msg.seconds || 5) * 1000, total: msg.seconds || 5 });
          } else if (msg.type === "chat") {
            setChat((cur) => [...cur, { ...msg, ts: Date.now() }].slice(-50));
          } else if (msg.type === "stroke") {
            remoteStrokes.current.push(msg.payload);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { if (!cancelled) retry = setTimeout(connect, 1500); };
      ws.onerror = () => { try { ws.close(); } catch { /* no-op */ } };
    };
    connect();
    return () => {
      cancelled = true;
      clearTimeout(retry);
      try { ws && ws.close(); } catch { /* no-op */ }
    };
    // eslint-disable-next-line
  }, [room?.code, token]);

  // ---------------- Sprint countdown ticker ----------------
  const [sprintLeft, setSprintLeft] = useState(0);
  useEffect(() => {
    if (!sprint) { setSprintLeft(0); return; }
    const tick = () => setSprintLeft(Math.max(0, Math.ceil((sprint.secondsAt - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 200);
    return () => clearInterval(t);
  }, [sprint]);
  // Clear sprint banner once round starts
  useEffect(() => { if (inRound && sprint) setSprint(null); }, [inRound, sprint]);

  // ---------------- Round timer ----------------

  useEffect(() => {
    if (!inRound || !room?.round_ends_at) { setTimeLeft(0); return; }
    const end = new Date(room.round_ends_at).getTime();
    const t = setInterval(() => {
      const left = Math.max(0, Math.round((end - Date.now()) / 1000));
      setTimeLeft(left);
    }, 500);
    return () => clearInterval(t);
  }, [inRound, room?.round_ends_at]);

  // ---------------- Canvas drawing ----------------

  // Resize canvas to fit container on mount; reset on round change
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
  }, [room?.round_number]);

  // Drain remote strokes
  useEffect(() => {
    let raf;
    const tick = () => {
      const c = canvasRef.current;
      if (c) {
        const ctx = c.getContext("2d");
        while (remoteStrokes.current.length) {
          const s = remoteStrokes.current.shift();
          if (s?.type === "clear") {
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, c.width, c.height);
          } else if (s?.type === "line") {
            ctx.strokeStyle = s.color || "#000";
            ctx.lineWidth = s.size || 4;
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.moveTo(s.x0, s.y0);
            ctx.lineTo(s.x1, s.y1);
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const sendStroke = (payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      ws.send(JSON.stringify({ type: "stroke", payload }));
    }
  };

  const handlePointer = (e, kind) => {
    if (!isDrawer || !inRound) return;
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const scaleX = c.width / rect.width;
    const scaleY = c.height / rect.height;
    const px = x * scaleX;
    const py = y * scaleY;
    if (kind === "down") {
      drawing.current = true;
      lastPt.current = { x: px, y: py };
    } else if (kind === "move" && drawing.current) {
      const ctx = c.getContext("2d");
      ctx.strokeStyle = color;
      ctx.lineWidth = brushSize;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(lastPt.current.x, lastPt.current.y);
      ctx.lineTo(px, py);
      ctx.stroke();
      sendStroke({ type: "line", x0: lastPt.current.x, y0: lastPt.current.y, x1: px, y1: py, color, size: brushSize });
      lastPt.current = { x: px, y: py };
    } else if (kind === "up") {
      drawing.current = false;
    }
  };

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    sendStroke({ type: "clear" });
  };

  // ---------------- UI ----------------

  if (mode === "lobby") {
    return (
      <div className="min-h-screen themed-bg">
        <header className="border-b-4 px-6 py-4 flex items-center justify-between themed-border" style={{ background: "var(--bq-header-bg)" }}>
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="brutal-btn inline-flex items-center gap-1">
            <ArrowLeft size={14} /> HUB
          </button>
          <h1 className="font-black uppercase tracking-tight text-lg themed flex items-center gap-2">
            <Pencil size={20} /> Office Pictionary
          </h1>
          <div style={{ width: 60 }} />
        </header>
        <main className="max-w-md mx-auto px-6 py-10 grid gap-4">
          <button data-testid="picti-create-btn" onClick={createRoom} className="brutal-btn p-6 text-xl font-black" style={{ background: "var(--bq-accent)" }}>
            CREATE A ROOM
          </button>
          <div className="brutal-card p-5">
            <h3 className="font-black uppercase mb-2">Join a room</h3>
            <div className="flex gap-2">
              <input data-testid="picti-join-input" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="ABC123" className="border-2 themed-border p-3 flex-1 font-mono-bq uppercase" style={{ background: "var(--bq-card-bg)" }}/>
              <button data-testid="picti-join-btn" onClick={joinRoom} className="brutal-btn">JOIN</button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen themed-bg">
      <header className="border-b-4 px-4 py-3 flex items-center justify-between themed-border" style={{ background: "var(--bq-header-bg)" }}>
        <button data-testid="back-btn" onClick={() => { setRoom(null); setMode("lobby"); }} className="brutal-btn inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Leave
        </button>
        <div className="text-center">
          <h1 className="font-black uppercase tracking-tight text-sm themed flex items-center gap-2 justify-center">
            <Pencil size={16} /> Room <span className="font-mono-bq">{room?.code}</span>
          </h1>
          <div className="text-xs" style={{ color: "var(--bq-muted)" }}>Round {room?.round_number || 0} / {room?.max_rounds}</div>
        </div>
        <div className="font-mono-bq font-black text-xl" data-testid="picti-timer">{inRound ? `${timeLeft}s` : "—"}</div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 grid lg:grid-cols-[1fr_280px] gap-4">
        {/* LEFT: canvas + prompt */}
        <section className="brutal-card p-3">
          <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
            <div data-testid="picti-prompt" className="font-black uppercase text-lg">
              {room?.state === "lobby" && !sprint && "Waiting for host to start…"}
              {room?.state === "lobby" && sprint && (
                <span data-testid="sprint-banner">⚡ Word Sprint in <span style={{color:"var(--bq-accent)"}}>{sprintLeft}s</span>…</span>
              )}
              {inRound && (isDrawer
                ? <>Draw: <span style={{ color: "var(--bq-accent)" }}>{room.word}</span></>
                : <>Guess the word: <span className="font-mono-bq tracking-widest">{room.word_hint}</span></>
              )}
              {room?.state === "round_end" && <>Round over. Word was: <span style={{ color: "var(--bq-accent)" }}>{room.word}</span></>}
              {room?.state === "game_end" && "🏆 Game over! Final standings →"}
            </div>
            {isDrawer && inRound && (
              <div className="flex items-center gap-2">
                {["#000000", "#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#a855f7"].map((c) => (
                  <button key={c} data-testid={`picti-color-${c}`} onClick={() => setColor(c)} className={`w-7 h-7 border-2 themed-border ${color === c ? "ring-2 ring-offset-1" : ""}`} style={{ background: c }}/>
                ))}
                <input data-testid="picti-brush" type="range" min={2} max={20} value={brushSize} onChange={(e) => setBrushSize(parseInt(e.target.value, 10))} className="w-24"/>
                <button data-testid="picti-clear" onClick={clearCanvas} className="brutal-btn inline-flex items-center gap-1 text-xs"><Eraser size={12}/> Clear</button>
              </div>
            )}
          </div>
          <div className="border-2 themed-border" style={{ background: "#fff" }}>
            <canvas
              ref={canvasRef}
              data-testid="picti-canvas"
              width={900}
              height={560}
              className="w-full block touch-none"
              style={{ cursor: isDrawer && inRound ? "crosshair" : "not-allowed" }}
              onMouseDown={(e) => handlePointer(e, "down")}
              onMouseMove={(e) => handlePointer(e, "move")}
              onMouseUp={(e) => handlePointer(e, "up")}
              onMouseLeave={(e) => handlePointer(e, "up")}
              onTouchStart={(e) => handlePointer(e, "down")}
              onTouchMove={(e) => handlePointer(e, "move")}
              onTouchEnd={(e) => handlePointer(e, "up")}
            />
          </div>

          {/* Host controls */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {room?.state === "lobby" && isHost && (
              <button data-testid="picti-start" disabled={(room.players?.length || 0) < 2} onClick={startGame} className="brutal-btn inline-flex items-center gap-1 disabled:opacity-50" style={{ background: "var(--bq-accent)" }}>
                <Play size={14}/> Start Game ({room.players?.length || 0} players, need ≥2)
              </button>
            )}
            {(room?.state === "round_end" || room?.state === "game_end") && isHost && room?.state !== "game_end" && (
              <button data-testid="picti-next" onClick={nextRound} className="brutal-btn inline-flex items-center gap-1" style={{ background: "var(--bq-accent)" }}>
                <FastForward size={14}/> Next Round
              </button>
            )}
            {room?.state === "game_end" && (
              <a
                data-testid="picti-share-card"
                href={`${API}/pictionary/room/${room.code}/winner-card?token=${token}`}
                target="_blank"
                rel="noreferrer"
                className="brutal-btn inline-flex items-center gap-1"
                style={{ background: "var(--bq-accent)" }}
              >
                <Trophy size={14}/> Share winner card
              </a>
            )}
            <div className="text-xs" style={{ color: "var(--bq-muted)" }}>
              {isDrawer ? ROUND_TIPS[(room?.round_number || 0) % ROUND_TIPS.length] : "Type your guess on the right →"}
            </div>
          </div>
        </section>

        {/* RIGHT: players + chat */}
        <aside className="grid gap-4 content-start">
          <div className="brutal-card p-3">
            <h3 className="text-sm font-black uppercase mb-2 flex items-center gap-1"><Trophy size={14}/> Scores</h3>
            <ul className="grid gap-1.5">
              {(room?.players || []).sort((a,b) => b.score - a.score).map((p) => (
                <li key={p.user_id} data-testid={`picti-player-${p.user_id}`} className="flex items-center gap-2 text-sm border-2 themed-border p-2" style={{ background: "var(--bq-card-bg)" }}>
                  {p.avatar_path ? (
                    <img src={`${API}/files/${p.avatar_path}`} alt="" className="w-6 h-6 object-cover border border-black"/>
                  ) : (
                    <div className="w-6 h-6 border border-black inline-flex items-center justify-center font-black text-xs" style={{ background: "var(--bq-accent2)" }}>{p.name[0]}</div>
                  )}
                  <div className="flex-1 min-w-0 truncate font-bold">{p.name}{p.is_self && " (you)"}</div>
                  {p.is_drawer && <span title="drawer"><Pencil size={12}/></span>}
                  <span className="font-mono-bq font-black">{p.score}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="brutal-card p-3">
            <h3 className="text-sm font-black uppercase mb-2 flex items-center gap-1"><Users size={14}/> Chat & Guesses</h3>
            <div data-testid="picti-chat" className="h-40 overflow-y-auto border-2 themed-border p-2 text-sm space-y-1" style={{ background: "var(--bq-card-bg)" }}>
              {chat.length === 0 && <p style={{ color: "var(--bq-muted)" }}>No guesses yet…</p>}
              {chat.map((m, i) => (
                <div key={`${m.ts}-${i}`} className={`${m.correct ? "font-black" : ""}`} style={{ color: m.correct ? "var(--bq-accent)" : "inherit" }}>
                  <span className="font-bold">{m.from}:</span> {m.correct ? "GOT IT! 🎯" : m.text}
                </div>
              ))}
            </div>
            {!isDrawer && inRound && (
              <div className="flex gap-1 mt-2">
                <input data-testid="picti-guess-input" value={guess} onChange={(e) => setGuess(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitGuess()} placeholder="Your guess…" className="border-2 themed-border p-2 flex-1 text-sm" style={{ background: "var(--bq-card-bg)" }}/>
                <button data-testid="picti-guess-btn" onClick={submitGuess} className="brutal-btn"><Send size={14}/></button>
              </div>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
}
